import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Money,
  toast,
} from '@mlm/ui';
import { api, getTenantFeatures } from '../lib/api';

/**
 * QoL Slice 3 — the member-typed `reference` is intentionally NOT delivered
 * to this page so the admin reads it from the proof picture and types it
 * back. `referenceProvided` shows whether the member sent one;
 * `adminTypedReference` is set once the admin commits a value through the
 * review modal, and acts as the "verified" chip in the list.
 */
type PendingPayment = {
  id: string;
  orderId: string;
  orderCode: string;
  orderType: string;
  buyerUserId: string;
  /** Set when several orders were paid with ONE proof (mixed-cart single payment). */
  checkoutGroupId: string | null;
  buyerName: string | null;
  method: string;
  amount: string;
  currency: string;
  referenceProvided: boolean;
  adminTypedReference: string | null;
  proofR2Key: string | null;
  scanStatus: 'pending' | 'clean' | 'infected';
  createdAt: string;
};

/** What the review dialog acts on: one payment, or a whole single-payment group. */
type ReviewTarget = { groupId: string | null; payments: PendingPayment[] };

type GroupedRow =
  | { kind: 'single'; payment: PendingPayment }
  | { kind: 'group'; groupId: string; payments: PendingPayment[] };

/**
 * Collapse payments whose orders share a checkoutGroupId (one proof, several
 * orders) into one row, preserving list order. A group with a single pending
 * row renders as a normal single row.
 */
function groupPayments(rows: PendingPayment[]): GroupedRow[] {
  const counts = new Map<string, number>();
  for (const p of rows) {
    if (p.checkoutGroupId) counts.set(p.checkoutGroupId, (counts.get(p.checkoutGroupId) ?? 0) + 1);
  }
  const out: GroupedRow[] = [];
  const emitted = new Set<string>();
  for (const p of rows) {
    const gid = p.checkoutGroupId;
    if (gid && (counts.get(gid) ?? 0) > 1) {
      if (emitted.has(gid)) continue;
      emitted.add(gid);
      out.push({
        kind: 'group',
        groupId: gid,
        payments: rows.filter((x) => x.checkoutGroupId === gid),
      });
    } else {
      out.push({ kind: 'single', payment: p });
    }
  }
  return out;
}

/** The row whose member-typed reference is verified for the whole group. */
function primaryOf(payments: PendingPayment[]): PendingPayment {
  return payments.find((p) => p.referenceProvided) ?? payments[0]!;
}

function sumAmount(payments: PendingPayment[]): string {
  return payments.reduce((s, p) => s + Number(p.amount), 0).toFixed(2);
}

function scanVariant(status: PendingPayment['scanStatus']): 'success' | 'destructive' | 'muted' {
  if (status === 'clean') return 'success';
  if (status === 'infected') return 'destructive';
  return 'muted';
}

export function PaymentsPage() {
  const qc = useQueryClient();
  const [reviewing, setReviewing] = useState<ReviewTarget | null>(null);
  const [proofErrorId, setProofErrorId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['payments-pending'],
    queryFn: async () => (await api.get<PendingPayment[]>('/payments/manual/pending')).data,
  });

  /**
   * Fetch a short-lived presigned R2 URL for the uploaded proof and open it in
   * a new tab. Proof files are sensitive (KYC-adjacent, they show bank/wallet
   * details), so we don't embed them inline or proxy them through admin SPA
   * storage; the URL self-expires in 10 minutes per the backend.
   */
  async function viewProof(paymentId: string) {
    setProofErrorId(null);
    try {
      const res = await api.get<{ url: string; expiresInSeconds: number }>(
        `/payments/manual/${paymentId}/proof-url`,
      );
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('proof-url failed', err);
      setProofErrorId(paymentId);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Manual Payments</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="p-6 text-sm text-destructive">Failed to load payments.</p>
        ) : !data?.length ? (
          <p className="p-6 text-sm text-muted-foreground">No pending payments.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b">
              <tr>
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">Order</th>
                <th className="px-4 py-2 font-medium">Method</th>
                <th className="px-4 py-2 font-medium text-right">Amount</th>
                <th className="px-4 py-2 font-medium">Reference</th>
                <th className="px-4 py-2 font-medium">Scan</th>
                <th className="px-4 py-2 font-medium">Proof</th>
                <th className="px-4 py-2 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {groupPayments(data).map((row) => {
                // A group shares one proof + one reference; we present the
                // primary row's reference/scan and the combined amount, with one
                // Review that approves/rejects every order in the group.
                const isGroup = row.kind === 'group';
                const payments = isGroup ? row.payments : [row.payment];
                const primary = primaryOf(payments);
                const anyInfected = payments.some((p) => p.scanStatus === 'infected');
                const target: ReviewTarget = {
                  groupId: isGroup ? row.groupId : null,
                  payments,
                };
                const key = isGroup ? `g:${row.groupId}` : primary.id;
                return (
                  <Fragment key={key}>
                    <tr className="border-b">
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {new Date(primary.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {isGroup ? (
                          <>
                            <div className="font-medium">
                              Single payment · {payments.length} orders
                            </div>
                            <div className="font-mono text-[11px] text-muted-foreground">
                              {payments.map((p) => p.orderCode).join(', ')}
                            </div>
                          </>
                        ) : (
                          <div className="font-mono">{primary.orderCode}</div>
                        )}
                        {primary.buyerName && (
                          <div className="text-muted-foreground">{primary.buyerName}</div>
                        )}
                        <Link
                          to={`/members/${primary.buyerUserId}`}
                          className="text-primary hover:underline"
                        >
                          View buyer
                        </Link>
                      </td>
                      <td className="px-4 py-2">{primary.method}</td>
                      <td className="px-4 py-2 text-right">
                        <Money
                          amount={sumAmount(payments)}
                          currency={primary.currency}
                          direction="neutral"
                        />
                      </td>
                      <td className="px-4 py-2">
                        {primary.adminTypedReference ? (
                          <div className="flex items-center gap-1.5">
                            <Badge variant="success" className="text-[10px]">
                              verified
                            </Badge>
                            <span className="font-mono text-xs">{primary.adminTypedReference}</span>
                          </div>
                        ) : primary.referenceProvided ? (
                          <Badge variant="warning" className="text-[10px]">
                            submitted
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant={scanVariant(primary.scanStatus)}>
                          {primary.scanStatus}
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!primary.proofR2Key}
                          onClick={() => viewProof(primary.id)}
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> View
                        </Button>
                        {proofErrorId === primary.id ? (
                          <p className="mt-1 text-xs text-destructive">
                            Could not load proof. File may be missing or link expired.
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          size="sm"
                          disabled={anyInfected}
                          onClick={() => setReviewing(target)}
                        >
                          Review…
                        </Button>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>

      <ReviewPaymentDialog
        target={reviewing}
        onClose={() => setReviewing(null)}
        onDone={() => {
          setReviewing(null);
          qc.invalidateQueries({ queryKey: ['payments-pending'] });
        }}
      />
    </Card>
  );
}

/**
 * QoL Slice 3 — review modal. Two-stage flow:
 *   1. Admin reads the reference number from the picture and types it into the
 *      input. Server checks uniqueness per method, then returns whether it
 *      matches the member-typed value. The member's value is hidden until
 *      this point.
 *   2. After verification, Approve / Reject buttons enable. Approval through a
 *      mismatch is allowed but writes an extra `reference_mismatch_approved`
 *      audit row.
 */
type VerifyResult = {
  match: boolean;
  adminTypedReference: string;
  memberTypedReference: string | null;
  alreadyVerified: boolean;
};

function ReviewPaymentDialog({
  target,
  onClose,
  onDone,
}: {
  target: ReviewTarget | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const payments = target?.payments ?? [];
  const primary = payments.length ? primaryOf(payments) : null;
  const isGroup = !!target?.groupId;
  const [adminRef, setAdminRef] = useState('');
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewError, setReviewError] = useState<string | null>(null);
  // "Approve anyway" — only offered when the tenant enabled the override AND
  // the reference is still unverified. Requires a written reason.
  const [forceMode, setForceMode] = useState(false);
  const [forceReason, setForceReason] = useState('');
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const forceAllowed = featuresQ.data?.manual_payment_force_approve === true;

  function reset() {
    setAdminRef('');
    setVerifyResult(null);
    setVerifyError(null);
    setRejectMode(false);
    setRejectReason('');
    setReviewError(null);
    setForceMode(false);
    setForceReason('');
  }

  const verify = useMutation({
    mutationFn: async () => {
      if (!primary) throw new Error('no_payment');
      // For a group, the reference is verified once on the primary payment.
      const res = await api.post<VerifyResult>(`/payments/manual/${primary.id}/verify-reference`, {
        adminTypedReference: adminRef.trim(),
      });
      return res.data;
    },
    onSuccess: (data) => {
      setVerifyResult(data);
      setVerifyError(null);
    },
    onError: (err: unknown) => {
      const data = (
        err as {
          response?: { data?: { error?: string; existingOrderCode?: string; message?: string } };
        }
      ).response?.data;
      if (data?.error === 'duplicate_admin_reference') {
        setVerifyError(
          data.existingOrderCode
            ? `That reference has already been approved on order ${data.existingOrderCode}.`
            : 'That reference has already been approved on another order.',
        );
      } else {
        setVerifyError(data?.message ?? 'Could not verify reference.');
      }
    },
  });

  const review = useMutation({
    mutationFn: async (decision: 'approved' | 'rejected') => {
      if (!primary) throw new Error('no_payment');
      const body: {
        decision: 'approved' | 'rejected';
        rejectionReason?: string;
        force?: boolean;
        forceReason?: string;
      } = { decision };
      if (decision === 'rejected') body.rejectionReason = rejectReason.trim() || undefined;
      // Only a genuinely unverified approval is forced; once verified there is
      // nothing to override and the server ignores the flag anyway.
      if (decision === 'approved' && forceMode && !verifyResult) {
        body.force = true;
        body.forceReason = forceReason.trim();
      }
      // Group review approves/rejects every order in the group at once.
      const url =
        isGroup && target?.groupId
          ? `/payments/manual/group/${target.groupId}/review`
          : `/payments/manual/${primary.id}/review`;
      await api.post(url, body);
    },
    onSuccess: (_data, decision) => {
      const n = payments.length;
      toast.success(
        decision === 'approved'
          ? n > 1
            ? `${n} payments approved.`
            : 'Payment approved.'
          : n > 1
            ? `${n} payments rejected.`
            : 'Payment rejected.',
      );
      reset();
      onDone();
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: { error?: string; message?: string } } }).response
        ?.data;
      setReviewError(data?.message ?? data?.error ?? 'Failed.');
    },
  });

  const verified = verifyResult !== null;
  const mismatch = verified && !verifyResult.match;

  return (
    <Dialog
      open={!!target}
      onOpenChange={(o) => {
        if (!o && !verify.isPending && !review.isPending) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Review payment{' '}
            {isGroup
              ? `· ${payments.length} orders`
              : primary?.orderCode
                ? `· ${primary.orderCode}`
                : ''}
          </DialogTitle>
        </DialogHeader>

        {primary && (
          <div className="space-y-3">
            {isGroup && (
              <div className="rounded-md border p-3 text-sm">
                <p className="mb-1 text-xs text-muted-foreground">
                  One proof covers {payments.length} orders — approving settles them all.
                </p>
                <ul className="divide-y">
                  {payments.map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-2 py-1">
                      <span className="font-mono text-xs">{p.orderCode}</span>
                      <Money amount={p.amount} currency={p.currency} direction="neutral" />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm border rounded-md p-3 bg-muted/30">
              <dt className="text-muted-foreground">Method</dt>
              <dd>{primary.method}</dd>
              <dt className="text-muted-foreground">{isGroup ? 'Total amount' : 'Amount'}</dt>
              <dd>
                <Money
                  amount={sumAmount(payments)}
                  currency={primary.currency}
                  direction="neutral"
                />
              </dd>
              <dt className="text-muted-foreground">Scan</dt>
              <dd>{primary.scanStatus}</dd>
            </dl>

            <p className="text-xs text-muted-foreground">
              View the proof picture, then type the reference number you read from it.{' '}
              {primary.referenceProvided
                ? 'The member submitted a reference; it will be revealed for comparison after you commit a value.'
                : 'The member did not submit a reference; only your typed value will be saved.'}
            </p>

            {!verified ? (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground" htmlFor="adminRef">
                  Reference from picture
                </label>
                <div className="flex gap-2">
                  <Input
                    id="adminRef"
                    value={adminRef}
                    onChange={(e) => setAdminRef(e.target.value)}
                    placeholder="e.g. 1234567890"
                    autoFocus
                    disabled={verify.isPending}
                  />
                  <Button
                    onClick={() => verify.mutate()}
                    disabled={!adminRef.trim() || verify.isPending}
                  >
                    {verify.isPending ? 'Verifying…' : 'Verify'}
                  </Button>
                </div>
                {verifyError && <p className="text-sm text-destructive">{verifyError}</p>}
                {forceAllowed && !forceMode && (
                  <button
                    type="button"
                    className="text-xs underline text-muted-foreground hover:text-foreground"
                    onClick={() => setForceMode(true)}
                  >
                    Can't read the reference? Approve anyway…
                  </button>
                )}
                {forceAllowed && forceMode && (
                  <div className="space-y-1 rounded-md border border-warning/40 bg-warning/10 p-3">
                    <p className="text-sm font-medium">⚠ Approving without verification</p>
                    <p className="text-xs text-muted-foreground">
                      This skips the reference check. Your reason is recorded in the audit log
                      against your account.
                    </p>
                    <Input
                      value={forceReason}
                      onChange={(e) => setForceReason(e.target.value)}
                      placeholder="Why? e.g. reference number cropped out of the screenshot"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="text-xs underline opacity-80 hover:opacity-100"
                      onClick={() => {
                        setForceMode(false);
                        setForceReason('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div
                className={
                  'rounded-md border p-3 text-sm ' +
                  (mismatch
                    ? 'border-warning/40 bg-warning/10 text-foreground'
                    : 'border-success/40 bg-success/10 text-foreground')
                }
              >
                {mismatch ? (
                  <>
                    <p className="font-medium mb-1">⚠ Mismatch</p>
                    <p>
                      You typed{' '}
                      <span className="font-mono">{verifyResult.adminTypedReference}</span>. The
                      member typed{' '}
                      <span className="font-mono">
                        {verifyResult.memberTypedReference ?? '— (none)'}
                      </span>
                      .
                    </p>
                    <p className="text-xs mt-1 text-muted-foreground">
                      Approving anyway will audit-log the mismatch.
                    </p>
                  </>
                ) : (
                  <p>
                    ✓ Match: <span className="font-mono">{verifyResult.adminTypedReference}</span>
                  </p>
                )}
                {/* Item 7: correct a mistyped reference before approving —
                    re-verifying overwrites the stored value. */}
                <button
                  type="button"
                  className="mt-1 text-xs underline opacity-80 hover:opacity-100"
                  onClick={() => {
                    setAdminRef(verifyResult.adminTypedReference);
                    setVerifyResult(null);
                    setVerifyError(null);
                  }}
                >
                  Change reference
                </button>
              </div>
            )}

            {rejectMode && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Rejection reason (optional)</label>
                <Input
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g. proof unreadable, ref doesn't match"
                  autoFocus
                />
              </div>
            )}

            {reviewError && <p className="text-sm text-destructive">{reviewError}</p>}
          </div>
        )}

        <DialogFooter>
          {!rejectMode ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  reset();
                  onClose();
                }}
                disabled={verify.isPending || review.isPending}
              >
                Close
              </Button>
              <Button
                variant="destructive"
                onClick={() => setRejectMode(true)}
                disabled={verify.isPending || review.isPending}
              >
                Reject…
              </Button>
              <Button
                onClick={() => review.mutate('approved')}
                // Unlocked by a verified reference, OR by an explicit forced
                // approval with a written reason.
                disabled={(!verified && !(forceMode && forceReason.trim())) || review.isPending}
                variant={!verified && forceMode ? 'outline' : undefined}
              >
                {review.isPending && review.variables === 'approved'
                  ? 'Approving…'
                  : !verified && forceMode
                    ? 'Approve without verifying'
                    : mismatch
                      ? 'Approve anyway'
                      : 'Approve'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setRejectMode(false)}
                disabled={review.isPending}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={() => review.mutate('rejected')}
                disabled={review.isPending}
              >
                {review.isPending ? 'Rejecting…' : 'Confirm reject'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
